import React, { useState, useContext, useEffect, useMemo, useRef, useCallback } from "react";
import { useHistory, useLocation } from "react-router-dom";
import UIkit from "uikit";
import { AppContexts } from "../../providers";
import SearchableDropdown from "../common/SearchableDropdown";
import BuilderAreaGuideLayoutPanel from "./area-guide";
import BuilderExtrasSectionsPanel from "./extras-guide";
import BuilderSettingsPanel from "./settings";
import BuilderLayoutPanel from "./layout";
import BuilderGuidePreview from "./guide_preview";
import PropertyGuideEditor from "../property_guide/editor/index";
import BuilderPropertyStep from "./onboarding/property-step";
import BuilderOnboardingShell from "./onboarding/shell";
import BuilderRecommendationStep from "./onboarding/recommendation-step";
import BuilderFirstBookingStep from "./onboarding/booking-step";
import BuilderSendStep from "./onboarding/send-step";
import BuilderAboutHomeSource from "./onboarding/about-home-source";
import RevisionControls from "./revision-controls";
import { getGuideOnboardingStep } from "./onboarding/steps";

const DESIGN_SECTIONS = [
  { id: "home", label: "Home Guide Template", icon: "home" },
  { id: "area", label: "Area Guide Sections", icon: "location" },
  { id: "extras", label: "Extras Sections", icon: "cart" },
  { id: "layout", label: "Layout", icon: "grid" },
  { id: "ai", label: "AI & Trip Planner", icon: "bolt" },
  { id: "guestbook", label: "Guest Access", icon: "settings" },
  { id: "subscription-popup", label: "Subscription", icon: "mail" },
];

const DESIGN_SECTION_LINKS = {
  area: { path: "/areas", label: "Open Area Guides" },
  extras: { path: "/extras", label: "Open Extras Guide" },
};

const STORAGE_KEY = "guideDesigner.uiState.v1";
const SECTION_QUERY_PARAM = "section";

const loadUIState = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
  } catch {
    return {};
  }
};

const getValidSectionId = (value) =>
  DESIGN_SECTIONS.some((section) => section.id === value) ? value : null;

const getSectionFromSearch = (search) => {
  const params = new URLSearchParams(search);
  return getValidSectionId(params.get(SECTION_QUERY_PARAM));
};

const buildSectionSearch = (search, sectionId) => {
  const params = new URLSearchParams(search);
  params.set(SECTION_QUERY_PARAM, sectionId);
  return `?${params.toString()}`;
};

const GuideDesigner = () => {
  const persisted = useMemo(loadUIState, []);
  const rentalContext = useContext(AppContexts.RentalsContext);
  const authContext = useContext(AppContexts.AuthContext);
  const previewRef = useRef(null);
  const pendingSaveRef = useRef(null);
  const registerSave = useCallback((save) => {
    pendingSaveRef.current = save;
    return () => {
      if (pendingSaveRef.current === save) pendingSaveRef.current = null;
    };
  }, []);
  const flushPendingSave = useCallback(
    () => pendingSaveRef.current?.flush() || Promise.resolve(),
    [],
  );
  const history = useHistory();
  const location = useLocation();
  useEffect(() => {
    let navigating = false;
    const unblock = history.block((nextLocation, action) => {
      if (!pendingSaveRef.current?.hasPending()) return undefined;
      if (navigating) return false;
      navigating = true;
      flushPendingSave().then(() => {
        if (action === "POP") {
          // history v4 cannot replay an arbitrary POP distance. Preserve the
          // history stack and let the next Back/Forward proceed.
          UIkit.notification("Guide changes saved. You can go back now.", "success");
          return;
        }
        unblock();
        history[action === "REPLACE" ? "replace" : "push"](nextLocation);
      }).catch(() => {
        UIkit.notification("Unable to save guide changes. Please retry before leaving.", "error");
      }).finally(() => { navigating = false; });
      return false;
    });
    return unblock;
  }, [flushPendingSave, history, location.pathname, location.search]);
  const requestedOnboardingStep = new URLSearchParams(location.search).get(
    "onboarding",
  );
  const hasRequestedOnboarding = !!requestedOnboardingStep;
  const hasStoredOnboarding =
    !!authContext.user?.guide_onboarding &&
    !authContext.user.guide_onboarding.completed_at &&
    !authContext.user.guide_onboarding.dismissed_at;
  const startsInOnboarding =
    (authContext.user?.details?.type === "onboarding" &&
      !authContext.user?.guide_onboarding?.completed_at &&
      !authContext.user?.guide_onboarding?.dismissed_at) ||
    hasStoredOnboarding ||
    hasRequestedOnboarding;

  // `setMode` is intentionally unused while the Master/Property toggle
  // is hidden. Keep the state so panels still receive `mode` and so
  // re-enabling the toggle is a one-line change.
  // eslint-disable-next-line no-unused-vars
  const [mode, setMode] = useState(persisted.mode || "property");
  const [selectedRentalId, setSelectedRentalId] = useState(
    persisted.selectedRentalId || "",
  );
  const [rentalsReady, setRentalsReady] = useState(
    !startsInOnboarding,
  );
  const [onboarding, setOnboarding] = useState(
    authContext.user?.guide_onboarding ||
      (startsInOnboarding
        ? { current_step: requestedOnboardingStep || "property" }
        : null),
  );
  const [onboardingRental, setOnboardingRental] = useState(null);
  const [onboardingSaving, setOnboardingSaving] = useState(false);
  const [onboardingError, setOnboardingError] = useState("");
  const initialSectionHandled = useRef(false);
  const [activeDesigner, setActiveDesigner] = useState(() => {
    const section = getSectionFromSearch(location.search) ||
      getValidSectionId(persisted.activeDesigner) || "home";
    return section === "ai" ? "home" : section;
  });

  useEffect(() => {
    document.title = "Guide Designer - RueBaRue";
    let mounted = true;
    const restoreOnboarding = async () => {
      const loadedRentals = (await rentalContext.get()) || [];
      if (!startsInOnboarding || !mounted) return;

      let progress = authContext.user?.guide_onboarding || onboarding;
      if (authContext.getGuideOnboarding) {
        try {
          progress = await authContext.getGuideOnboarding();
        } catch {
          setOnboardingError(
            "We couldn't load your saved progress. You can still continue.",
          );
        }
      }

      if (!mounted) return;
      if (progress) setOnboarding(progress);

      const availableRentals = Array.isArray(loadedRentals)
        ? loadedRentals
        : rentalContext.rentals || [];
      let restoredRental = availableRentals.find(
        (rental) =>
          String(rental.rental_id) === String(progress?.rental_id),
      );

      if (
        !restoredRental &&
        progress?.rental_id &&
        rentalContext.getRental
      ) {
        try {
          restoredRental = await rentalContext.getRental(progress.rental_id);
        } catch {
          restoredRental = null;
        }
      }

      if (!mounted) return;
      if (restoredRental) {
        setOnboardingRental(restoredRental);
        setSelectedRentalId(String(restoredRental.rental_id));
      }
      setRentalsReady(true);
    };

    restoreOnboarding().catch(() => {
      if (mounted) setRentalsReady(true);
    });

    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ mode, selectedRentalId, activeDesigner }),
      );
    } catch {
      /* ignore quota errors */
    }
  }, [mode, selectedRentalId, activeDesigner]);

  useEffect(() => {
    const querySection = getSectionFromSearch(location.search);

    if (!initialSectionHandled.current) {
      initialSectionHandled.current = true;
      // Opening an old AI URL starts at the template. Selecting AI within the
      // designer still works normally after this initial navigation.
      if (querySection === "ai" && !startsInOnboarding) {
        history.replace({
          ...location,
          search: buildSectionSearch(location.search, "home"),
        });
        return;
      }
    }

    if (querySection && querySection !== activeDesigner) {
      setActiveDesigner(querySection);
    }
  }, [location, activeDesigner, history, startsInOnboarding]);

  useEffect(() => {
    const querySection = getSectionFromSearch(location.search);

    if (querySection) {
      return;
    }

    history.replace({
      pathname: location.pathname,
      search: buildSectionSearch(location.search, activeDesigner),
    });
  }, [activeDesigner, history, location.pathname, location.search]);

  const rentals = useMemo(
    () =>
      [...(rentalContext.rentals || [])].sort((a, b) =>
        (a.name || a.pms_id || "").localeCompare(b.name || b.pms_id || ""),
      ),
    [rentalContext.rentals],
  );

  const isOnboardingFlow =
    startsInOnboarding ||
    (!!onboarding && !onboarding.completed_at && !onboarding.dismissed_at) ||
    hasRequestedOnboarding;
  const currentOnboardingStep = getGuideOnboardingStep(
    onboarding?.current_step || requestedOnboardingStep || "property",
  );

  const updateOnboardingLocation = (step) => {
    const params = new URLSearchParams(location.search);
    params.set("onboarding", step.id);
    params.set("section", step.section);
    history.replace({
      pathname: location.pathname,
      search: `?${params.toString()}`,
    });
  };

  const handleOnboardingStepChange = async (step, extra = {}) => {
    setOnboardingError("");
    setOnboardingSaving(true);
    try {
      await flushPendingSave();
    } catch {
      setOnboardingError("We couldn't save your guide changes. Please retry before continuing.");
      setOnboardingSaving(false);
      return;
    }
    setOnboarding((current) => ({
      ...(current || {}),
      ...extra,
      current_step: step.id,
    }));
    setActiveDesigner(step.section);
    updateOnboardingLocation(step);

    try {
      const saved = await authContext.updateGuideOnboarding({
        current_step: step.id,
        ...extra,
      });
      setOnboarding(saved);
    } catch (error) {
      console.warn("Unable to save the onboarding step position", error);
    } finally {
      setOnboardingSaving(false);
    }
  };

  const handleFirstPropertyComplete = async (result) => {
    if (result?.rental) {
      setOnboardingRental(result.rental);
      rentalContext.setRentals([result.rental]);
      setSelectedRentalId(String(result.rental.rental_id));
    } else {
      await rentalContext.get();
    }

    setOnboarding(result.onboarding || { current_step: "branding" });
    updateOnboardingLocation(getGuideOnboardingStep("branding"));
    setActiveDesigner("layout");
    await authContext.refresh();
  };

  const handleFirstBookingSaved = async (reservation) => {
    const sendStep = getGuideOnboardingStep("send");
    await handleOnboardingStepChange(sendStep, {
      guest_reservation_id: reservation.guest_reservation_id,
    });
  };

  const handleGuideSent = async () => {
    try {
      const saved = await authContext.updateGuideOnboarding({
        current_step: "send",
        completed: true,
      });
      setOnboarding(saved);
      await authContext.refresh();
    } catch {
      setOnboardingError(
        "The guide was sent, but onboarding completion was not saved.",
      );
    }
  };

  const handleExitOnboarding = async () => {
    const exitStep = selectedRental
      ? currentOnboardingStep
      : getGuideOnboardingStep("property");

    setOnboardingError("");
    setOnboardingSaving(true);
    try {
      await flushPendingSave();
      const saved = await authContext.updateGuideOnboarding({
        current_step: exitStep.id,
        dismissed: true,
      });
      setOnboarding(saved);
      await authContext.refresh();
      history.replace("/");
    } catch {
      setOnboardingError(
        "We couldn't exit onboarding. Please try again.",
      );
    } finally {
      setOnboardingSaving(false);
    }
  };

  const selectedRental = useMemo(
    () =>
      rentals.find((r) => String(r.rental_id) === String(selectedRentalId)) ||
      rentals.find(
        (r) => String(r.rental_id) === String(onboarding?.rental_id),
      ) ||
      (String(onboardingRental?.rental_id) === String(selectedRentalId)
        ? onboardingRental
        : null) ||
      (isOnboardingFlow ? rentals[0] || null : null) ||
      null,
    [
      isOnboardingFlow,
      onboarding?.rental_id,
      onboardingRental,
      rentals,
      selectedRentalId,
    ],
  );

  // Auto-select first rental once rentals load (so the preview has something)
  useEffect(() => {
    const rentalToSelect = selectedRental || rentals[0] || null;
    if (
      rentalToSelect &&
      String(rentalToSelect.rental_id) !== String(selectedRentalId)
    ) {
      setSelectedRentalId(String(rentalToSelect.rental_id));
    }
  }, [rentals, selectedRental, selectedRentalId]);

  const refreshPreview = () => {
    previewRef.current && previewRef.current.refresh();
  };

  const handleDesignerSelect = async (sectionId) => {
    if (sectionId === activeDesigner) {
      return;
    }

    try {
      await flushPendingSave();
    } catch {
      UIkit.notification("Unable to save guide changes. Please retry before switching sections.", "error");
      return;
    }

    const openEditors = Array.from(
      document.querySelectorAll("[data-guide-editor-offcanvas]"),
    )
      .map((element) => UIkit.offcanvas(element))
      .filter((offcanvas) => offcanvas.isToggled());
    await Promise.all(
      openEditors.map((offcanvas) => offcanvas.hide()),
    );

    setActiveDesigner(sectionId);
    history.replace({
      pathname: location.pathname,
      search: buildSectionSearch(location.search, sectionId),
    });
  };

  const isExpandedDesignerPanel = activeDesigner === "home";
  const activeSection = DESIGN_SECTIONS.find((section) => section.id === activeDesigner);
  const activeSectionLink = DESIGN_SECTION_LINKS[activeDesigner];

  const renderDesignerPanel = () => {
    const sharedProps = {
      rental: selectedRental,
      mode,
      onSaved: refreshPreview,
      onRegisterSave: registerSave,
    };
    switch (activeDesigner) {
      case "home":
        return (
          <PropertyGuideEditor
            embedded
            onSaved={refreshPreview}
            onRegisterSave={registerSave}
          />
        );
      case "area":
        return <BuilderAreaGuideLayoutPanel {...sharedProps} />;
      case "extras":
        return <BuilderExtrasSectionsPanel {...sharedProps} />;
      case "guestbook":
        return <BuilderSettingsPanel {...sharedProps} section="guestbook" />;
      case "subscription-popup":
        return (
          <BuilderSettingsPanel
            {...sharedProps}
            section="subscription-popup"
          />
        );
      case "layout":
        return <BuilderLayoutPanel {...sharedProps} />;
      case "ai":
        return <BuilderLayoutPanel {...sharedProps} panelFocus="ai" />;
      default:
        return null;
    }
  };

  const renderOnboardingPanel = (step = currentOnboardingStep) => {
    const sharedProps = {
      rental: selectedRental,
      mode,
      onSaved: refreshPreview,
      onRegisterSave: registerSave,
    };

    if (step.id === "property") {
      return <BuilderPropertyStep onComplete={handleFirstPropertyComplete} />;
    }
    if (["branding", "layout"].includes(step.id)) {
      return (
        <div className="guide-onboarding-editor-preview">
          <BuilderLayoutPanel
            {...sharedProps}
            onboardingFocus={step.id}
            hideRevisionControls
          />
          <BuilderGuidePreview
            ref={previewRef}
            rental={selectedRental}
            activeDesigner="layout"
          />
        </div>
      );
    }
    if (step.tabType || step.id === "add-home-section") {
      return (
        <div className="guide-onboarding-editor-preview">
          <div>
            {step.id === "about-home" && (
              <BuilderAboutHomeSource onboarding={onboarding} />
            )}
            <PropertyGuideEditor
              embedded
              focusedTabType={step.tabType || ""}
              onSaved={refreshPreview}
            />
          </div>
          <BuilderGuidePreview
            ref={previewRef}
            rental={selectedRental}
            activeDesigner="home"
          />
        </div>
      );
    }
    if (step.categoryType) {
      return (
        <div className="guide-onboarding-editor-preview">
          <BuilderRecommendationStep
            rental={selectedRental}
            categoryType={step.categoryType}
            label={step.label}
          />
          <BuilderGuidePreview
            ref={previewRef}
            rental={selectedRental}
            activeDesigner="area"
          />
        </div>
      );
    }
    if (step.id === "add-area-section") {
      return (
        <div className="guide-onboarding-editor-preview">
          <BuilderAreaGuideLayoutPanel {...sharedProps} />
          <BuilderGuidePreview
            ref={previewRef}
            rental={selectedRental}
            activeDesigner="area"
          />
        </div>
      );
    }
    if (step.id === "preview-publish") {
      return (
        <div className="guide-onboarding-publish-panel">
          <BuilderGuidePreview
            ref={previewRef}
            rental={selectedRental}
            activeDesigner="home"
          />
          <div className="guide-onboarding-publish-actions">
            <h3>Ready to share?</h3>
            <p>
              Review the guide, then publish this version. You can continue
              editing and publish updates later.
            </p>
            <RevisionControls
              onBeforeSave={flushPendingSave}
              onPublished={() =>
                handleOnboardingStepChange(
                  getGuideOnboardingStep("first-booking"),
                )
              }
            />
          </div>
        </div>
      );
    }
    if (step.id === "first-booking") {
      return (
        <BuilderFirstBookingStep
          rental={selectedRental}
          onBooked={handleFirstBookingSaved}
        />
      );
    }
    if (step.id === "send") {
      return (
        <BuilderSendStep
          rental={selectedRental}
          onboarding={onboarding}
          onSent={handleGuideSent}
        />
      );
    }
    return null;
  };

  if (isOnboardingFlow && !rentalsReady) {
    return (
      <section id="guide-designer">
        <div className="builder-panel-loading" role="status">
          <span data-uk-spinner="ratio: 1" />
          <p>Preparing your guide builder…</p>
        </div>
      </section>
    );
  }

  if (isOnboardingFlow) {
    const effectiveStep =
      !selectedRental && !onboarding?.rental_id
        ? getGuideOnboardingStep("property")
        : currentOnboardingStep;
    const hideActions = [
      "property",
      "preview-publish",
      "first-booking",
      "send",
    ].includes(effectiveStep.id);

    return (
      <section id="guide-designer">
        <div id="hms-page-title">
          <div className="guide-onboarding-brand-header">
            <div className="guide-onboarding-brand-identity">
              <img
                src="/images/ruebarue-logo-full.svg"
                alt="RueBaRue"
              />
              <h1 className="uk-heading-small">Build your guest guide</h1>
            </div>
            <button
              type="button"
              className="uk-button uk-button-text guide-onboarding-exit"
              onClick={handleExitOnboarding}
              disabled={onboardingSaving}
              title="Your saved work will remain available"
            >
              {onboardingSaving ? "Exiting…" : "Exit onboarding"}
            </button>
          </div>
        </div>
        <div id="hms-main-body" className="guide-designer-main">
          {onboardingError && (
            <div className="uk-alert-warning guide-onboarding-status" role="alert">
              {onboardingError}
            </div>
          )}
          <BuilderOnboardingShell
            currentStep={effectiveStep}
            rental={selectedRental}
            allowStepNavigation={!!selectedRental}
            onStepChange={handleOnboardingStepChange}
            onFinish={handleGuideSent}
            hideActions={hideActions}
            actionBusy={onboardingSaving}
          >
            {renderOnboardingPanel(effectiveStep)}
          </BuilderOnboardingShell>
        </div>
      </section>
    );
  }

  return (
    <section id="guide-designer">
      <div id="hms-page-title">
        <div className="guide-designer-page-title-row">
          <h1 className="uk-heading-small">Guide Designer</h1>

          <div className="guide-designer-toolbar">
            <div className="guide-designer-toolbar-controls">
              {/*
                Master vs Property mode is intentionally hidden until the
                Master save path and preview URL are wired up. The `mode`
                state is still passed to panels so we can re-enable this
                radio without touching the rest of the builder.

                <div className="guide-designer-mode">
                  <label className="uk-margin-small-right">Mode:</label>
                  <label className="uk-margin-small-right">
                    <input className="uk-radio" type="radio" name="mode"
                      value="master" checked={mode === "master"}
                      onChange={() => setMode("master")} /> Master
                  </label>
                  <label>
                    <input className="uk-radio" type="radio" name="mode"
                      value="property" checked={mode === "property"}
                      onChange={() => setMode("property")} /> Property
                  </label>
                </div>
              */}

              {mode === "property" && activeDesigner !== "home" && (
                <div className="guide-designer-property-select">
                  <SearchableDropdown
                    value={selectedRentalId}
                    onChange={(val) => setSelectedRentalId(val)}
                    placeholder="Select a property..."
                    options={[
                      { value: "", label: "Select a property..." },
                      ...rentals.map((r) => ({
                        value: String(r.rental_id),
                        label:
                          r.name && r.pms_id
                            ? `${r.name} (${r.pms_id})`
                            : r.name || r.pms_id || `#${r.rental_id}`,
                      })),
                    ]}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div id="hms-main-body" className="guide-designer-main">
        {/* Main layout: section rail | designer | optional preview */}
        <div
          className={`guide-designer-layout${
            isExpandedDesignerPanel ? " is-home-editor" : ""
          }`}
        >
          {/* LEFT: section rail */}
          <aside
            className="guide-designer-rail-wrap"
            aria-label="Designer sections"
          >
            <ul className="guide-designer-rail">
              {DESIGN_SECTIONS.map((section) => (
                <li
                  key={section.id}
                  className={activeDesigner === section.id ? "is-active" : ""}
                >
                  <button
                    type="button"
                    className="guide-designer-rail-item"
                    onClick={() => handleDesignerSelect(section.id)}
                    title={section.label}
                    data-uk-tooltip={`title: ${section.label}; pos: right`}
                    aria-current={
                      activeDesigner === section.id ? "page" : undefined
                    }
                  >
                    <span className="guide-designer-rail-icon">
                      <span
                        data-uk-icon={`icon: ${section.icon}; ratio: 0.95`}
                      />
                    </span>
                    <span className="guide-designer-rail-label">
                      {section.label}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </aside>

          {/* MIDDLE: designer form */}
          <div
            className={`guide-designer-form-col${
              isExpandedDesignerPanel ? " is-expanded" : ""
            }`}
          >
            {isExpandedDesignerPanel ? (
              <div className="guide-designer-embedded-panel">
                {renderDesignerPanel()}
              </div>
            ) : (
              <div className="uk-card uk-card-default uk-card-small guide-designer-card">
                <div className="uk-card-header guide-designer-card-header">
                  <h3>{activeSection?.label}</h3>
                  {activeSectionLink && (
                    <button
                      type="button"
                      className="uk-button uk-button-text guide-designer-header-link"
                      onClick={() => history.push(activeSectionLink.path)}
                    >
                      {activeSectionLink.label}
                    </button>
                  )}
                </div>
                <div className="uk-card-body guide-designer-card-body">
                  {renderDesignerPanel()}
                </div>
              </div>
            )}
          </div>

          {!isExpandedDesignerPanel && (
            <div className="guide-designer-preview-col">
              <BuilderGuidePreview
                ref={previewRef}
                rental={selectedRental}
                rentalId={selectedRentalId}
                activeDesigner={activeDesigner}
              />
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default GuideDesigner;
